import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Switch } from '../../../components/ui/switch';
import { EmailTagInput } from '../../../components/ui/email-tag-input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';
import { Printer, Mail, Settings, RefreshCw, Send } from 'lucide-react';
import { conciliacionProveedoresApi } from '../../../lib/api';
import { triggerPrint } from '../../../lib/printUtils';

const ESTADO_META = {
  faltante: { short: 'Faltante', color: '#dc2626', bg: '#fef2f2', border: '#fecaca' },
  match_parcial: { short: 'Dif. monto', color: '#d97706', bg: '#fffbeb', border: '#fde68a' },
  en_rcv_pendiente_contab: { short: 'En RCV s/contab.', color: '#2563eb', bg: '#eff6ff', border: '#bfdbfe' },
};
const clp = (n) => `$${Math.round(Number(n) || 0).toLocaleString('es-CL')}`;
const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

export function InformePendientesSection({ user }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [configOpen, setConfigOpen] = useState(false);
  const [cfg, setCfg] = useState({ enabled: true, emails_to: [], emails_cc: [] });
  const [savingCfg, setSavingCfg] = useState(false);
  const [sending, setSending] = useState(false);

  const canConfig = ['admin', 'administrador', 'gerencia', 'contabilidad', 'finanzas']
    .includes(String(user?.rol || user?.role || '').toLowerCase());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await conciliacionProveedoresApi.getInformePendientes();
      setData(d);
    } catch (e) {
      toast.error('No se pudo cargar el informe', { description: e?.response?.data?.detail || '' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data: d } = await conciliacionProveedoresApi.getInformePendientes();
        if (active) setData(d);
      } catch (e) {
        toast.error('No se pudo cargar el informe', { description: e?.response?.data?.detail || '' });
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  const openConfig = async () => {
    try {
      const { data: c } = await conciliacionProveedoresApi.getInformeConfig();
      setCfg({ enabled: c.enabled, emails_to: c.emails_to || [], emails_cc: c.emails_cc || [] });
      setConfigOpen(true);
    } catch {
      toast.error('No se pudo cargar la configuración');
    }
  };

  const saveConfig = async () => {
    setSavingCfg(true);
    try {
      await conciliacionProveedoresApi.setInformeConfig(cfg);
      toast.success('Configuración guardada');
      setConfigOpen(false);
    } catch (e) {
      toast.error('No se pudo guardar', { description: e?.response?.data?.detail || '' });
    } finally {
      setSavingCfg(false);
    }
  };

  const enviarAhora = async () => {
    setSending(true);
    try {
      const { data: r } = await conciliacionProveedoresApi.enviarInformeAhora();
      if (r.email_enviado) {
        toast.success('Informe enviado por correo', { description: `${r.items} pendientes · ${clp(r.monto_total_pendiente)}` });
      } else {
        toast.warning('No se envió el correo', { description: r.email_omitido_motivo || 'Sin pendientes o correo no configurado' });
      }
    } catch (e) {
      toast.error('Error al enviar', { description: e?.response?.data?.detail || '' });
    } finally {
      setSending(false);
    }
  };

  const imprimir = () => {
    if (!data) return;
    const t = data.totales;
    const hoy = new Date().toLocaleDateString('es-CL');
    const groups = data.proveedores.map((g) => {
      const rows = g.items.map((it) => {
        const m = ESTADO_META[it.estado] || {};
        return `<tr>
          <td class="c"><span class="tag" style="background:${m.color}">${esc(m.short)}</span></td>
          <td class="mono">${esc(it.folio)}</td>
          <td>${esc(it.fecha) || '—'}</td>
          <td class="r mono">${clp(it.monto_pendiente)}</td>
          <td class="obs">${esc(it.observacion).slice(0, 160)}</td>
        </tr>`;
      }).join('');
      const head = `<tr class="grp"><td colspan="5">▸ ${esc(g.proveedor)}${g.rut ? ` <span class="grut">· ${esc(g.rut)}</span>` : ''}</td></tr>`;
      const sub = `<tr class="subt"><td colspan="3" class="r">Subtotal ${esc(g.proveedor)} (${g.items.length})</td><td class="r mono">${clp(g.subtotales.monto_pendiente)}</td><td></td></tr>`;
      return head + rows + sub;
    }).join('');

    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Informe Pendientes Conciliación</title>
    <style>
      @page { size: letter portrait; margin: 12mm; }
      * { box-sizing: border-box; }
      body { font-family: Arial, Helvetica, sans-serif; color:#1a202c; margin:0; }
      .head { background:#1a365d; color:#fff; padding:12px 16px; border-radius:8px 8px 0 0; }
      .head h1 { margin:0; font-size:16px; }
      .head p { margin:3px 0 0; font-size:11px; color:#cbd5e0; }
      .kpis { display:flex; gap:8px; margin:10px 0; }
      .kpi { flex:1; border:1px solid #e2e8f0; border-radius:6px; padding:6px; text-align:center; }
      .kpi .l { font-size:9px; color:#475569; }
      .kpi .v { font-size:17px; font-weight:800; }
      .kpi .m { font-size:9px; color:#475569; font-family:monospace; }
      table { width:100%; border-collapse:collapse; font-size:9.5px; }
      thead th { background:#e2e8f0; padding:4px 6px; text-align:left; border-bottom:1px solid #94a3b8; }
      td { padding:3px 6px; border-bottom:1px solid #edf2f7; vertical-align:top; }
      .c { text-align:center; } .r { text-align:right; } .mono { font-family:monospace; }
      .obs { font-size:8.5px; color:#555; }
      .tag { display:inline-block; color:#fff; padding:1px 5px; border-radius:7px; font-size:7.5px; font-weight:700; }
      tr.grp td { background:#1f2d3d; color:#fff; font-weight:700; font-size:10px; padding:4px 6px; }
      .grut { font-weight:400; color:#cbd5e0; }
      tr.subt td { background:#f1f5f9; font-weight:700; font-size:9px; color:#334155; }
      tr.totals td { background:#1a365d; color:#fff; font-weight:700; font-size:11px; padding:6px; }
      .legend { font-size:8px; color:#475569; margin:6px 0; }
    </style></head><body>
    <div class="head"><h1>CHIRINO STEEL · Informe de Pendientes de Conciliación</h1>
      <p>Consolidado de todos los proveedores — ${hoy}</p></div>
    <div class="kpis">
      <div class="kpi" style="background:#fef2f2;"><div class="l">🔴 Faltantes</div><div class="v" style="color:#dc2626">${t.faltante.count}</div><div class="m">${clp(t.faltante.monto)}</div></div>
      <div class="kpi" style="background:#fffbeb;"><div class="l">🟠 Dif. monto</div><div class="v" style="color:#d97706">${t.match_parcial.count}</div><div class="m">${clp(t.match_parcial.monto)}</div></div>
      <div class="kpi" style="background:#eff6ff;"><div class="l">🔵 En RCV s/contab.</div><div class="v" style="color:#2563eb">${t.en_rcv_pendiente_contab.count}</div><div class="m">${clp(t.en_rcv_pendiente_contab.monto)}</div></div>
      <div class="kpi" style="background:#f8fafc;"><div class="l">Total pendiente</div><div class="v" style="color:#0f172a">${clp(t.monto_total_pendiente)}</div><div class="m">${t.proveedores} prov · ${t.items} ítems</div></div>
    </div>
    <table>
      <thead><tr><th class="c" style="width:70px;">Tipo</th><th style="width:72px;">Folio</th><th style="width:70px;">Fecha</th><th class="r" style="width:90px;">Monto pend.</th><th>Observación</th></tr></thead>
      <tbody>
        ${groups || '<tr><td colspan="5" style="text-align:center;padding:14px;color:#16a34a;font-weight:600;">✅ Sin pendientes de conciliación.</td></tr>'}
        <tr class="totals"><td colspan="3" class="r">TOTAL PENDIENTE (${t.items})</td><td class="r mono">${clp(t.monto_total_pendiente)}</td><td></td></tr>
      </tbody>
    </table>
    <p class="legend">🔴 Faltante: en estado de cuenta del proveedor pero no en Alegra/RCV · 🟠 Dif. monto: diferencia de monto · 🔵 En RCV s/contab.: existe en RCV pero falta contabilizar.</p>
    </body></html>`;
    // triggerPrint YA dispara la impresión (evita doble diálogo). Carlos, Jun 2026.
    triggerPrint(html);
  };

  const solicitarEstado = (rut) => {
    navigate(`/contabilidad/solicitar-estado-cuenta?rut=${encodeURIComponent(rut || '')}`);
  };

  const t = data?.totales;

  return (
    <div className="space-y-4" data-testid="informe-pendientes-section">
      {/* Toolbar */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="text-base font-semibold">Informe de Pendientes de Conciliación</h3>
          <p className="text-xs text-muted-foreground">Consolidado de faltantes, diferencias de monto y facturas en RCV sin contabilizar — todos los proveedores en una vista.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button size="sm" variant="outline" className="gap-1 h-8" onClick={load} disabled={loading} data-testid="informe-refresh">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Actualizar
          </Button>
          <Button size="sm" variant="outline" className="gap-1 h-8" onClick={imprimir} disabled={!data} data-testid="informe-imprimir">
            <Printer className="w-3.5 h-3.5" /> Imprimir
          </Button>
          {canConfig && (
            <>
              <Button size="sm" className="gap-1 h-8 bg-blue-600 hover:bg-blue-700" onClick={enviarAhora} disabled={sending} data-testid="informe-enviar">
                <Send className="w-3.5 h-3.5" /> {sending ? 'Enviando…' : 'Enviar correo ahora'}
              </Button>
              <Button size="sm" variant="outline" className="gap-1 h-8" onClick={openConfig} data-testid="informe-config">
                <Settings className="w-3.5 h-3.5" /> Configurar correo
              </Button>
            </>
          )}
        </div>
      </div>

      {/* KPIs */}
      {t && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="informe-kpis">
          {[
            { k: 'faltante', label: '🔴 Faltantes', m: ESTADO_META.faltante },
            { k: 'match_parcial', label: '🟠 Diferencia de monto', m: ESTADO_META.match_parcial },
            { k: 'en_rcv_pendiente_contab', label: '🔵 En RCV sin contabilizar', m: ESTADO_META.en_rcv_pendiente_contab },
          ].map(({ k, label, m }) => (
            <div key={k} className="rounded-lg border p-3" style={{ background: m.bg, borderColor: m.border }}>
              <div className="text-xs" style={{ color: m.color }}>{label}</div>
              <div className="text-2xl font-extrabold" style={{ color: m.color }}>{t[k].count}</div>
              <div className="text-xs font-mono" style={{ color: m.color }}>{clp(t[k].monto)}</div>
            </div>
          ))}
          <div className="rounded-lg border p-3 bg-slate-50 border-slate-200">
            <div className="text-xs text-slate-600">Total pendiente</div>
            <div className="text-2xl font-extrabold text-slate-900">{clp(t.monto_total_pendiente)}</div>
            <div className="text-xs text-slate-600">{t.proveedores} prov · {t.items} ítems</div>
          </div>
        </div>
      )}

      {/* Tabla agrupada */}
      <div className="border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted">
            <tr className="text-left text-xs">
              <th className="p-2 w-28">Tipo</th>
              <th className="p-2 w-24">Folio</th>
              <th className="p-2 w-24">Fecha</th>
              <th className="p-2 text-right w-28">Monto pend.</th>
              <th className="p-2">Observación</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={5} className="p-6 text-center text-muted-foreground">Cargando…</td></tr>
            )}
            {!loading && data && data.proveedores.length === 0 && (
              <tr><td colSpan={5} className="p-6 text-center text-green-600 font-medium" data-testid="informe-empty">✅ Sin pendientes de conciliación.</td></tr>
            )}
            {!loading && data && data.proveedores.map((g) => (
              <GroupRows key={`${g.rut}-${g.proveedor}`} g={g} onSolicitar={solicitarEstado} />
            ))}
          </tbody>
        </table>
      </div>

      {/* Config dialog */}
      <Dialog open={configOpen} onOpenChange={setConfigOpen}>
        <DialogContent data-testid="informe-config-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Mail className="w-4 h-4" /> Correo diario del informe</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-sm">
            <div className="flex items-center justify-between rounded-md border p-2">
              <div>
                <div className="font-medium">Envío automático diario (08:00)</div>
                <div className="text-xs text-muted-foreground">Se envía solo si hay pendientes.</div>
              </div>
              <Switch checked={cfg.enabled} onCheckedChange={(v) => setCfg((c) => ({ ...c, enabled: v }))} data-testid="informe-cfg-enabled" />
            </div>
            <div>
              <label className="text-xs font-medium">Destinatarios (Para)</label>
              <EmailTagInput emails={cfg.emails_to} onChange={(v) => setCfg((c) => ({ ...c, emails_to: v }))} />
            </div>
            <div>
              <label className="text-xs font-medium">Copia (CC) — opcional</label>
              <EmailTagInput emails={cfg.emails_cc} onChange={(v) => setCfg((c) => ({ ...c, emails_cc: v }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfigOpen(false)} disabled={savingCfg}>Cancelar</Button>
            <Button onClick={saveConfig} disabled={savingCfg || cfg.emails_to.length === 0} data-testid="informe-cfg-save">
              {savingCfg ? 'Guardando…' : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const GroupRows = ({ g, onSolicitar }) => (
  <>
    <tr className="bg-slate-800 text-white">
      <td colSpan={5} className="p-2 font-semibold text-xs" data-testid={`informe-grupo-${g.rut}`}>
        <div className="flex items-center justify-between gap-2">
          <span>▸ {g.proveedor}{g.rut ? <span className="font-normal text-slate-300"> · {g.rut}</span> : null}</span>
          <button
            type="button"
            onClick={() => onSolicitar(g.rut)}
            className="inline-flex items-center gap-1 bg-blue-600 hover:bg-blue-500 text-white text-[10px] font-medium px-2 py-1 rounded-full transition-colors shrink-0"
            data-testid={`informe-solicitar-${g.rut}`}
            title="Solicitar estado de cuenta a este proveedor"
          >
            <Mail className="w-3 h-3" /> Solicitar estado de cuenta
          </button>
        </div>
      </td>
    </tr>
    {g.items.map((it, i) => {
      const m = ESTADO_META[it.estado] || {};
      return (
        <tr key={i} className="border-b">
          <td className="p-2">
            <span className="text-[10px] font-semibold text-white rounded px-1.5 py-0.5" style={{ background: m.color }}>{m.short}</span>
          </td>
          <td className="p-2 font-mono text-xs">{it.folio}</td>
          <td className="p-2 text-xs">{it.fecha || '—'}</td>
          <td className="p-2 text-right font-mono text-xs font-semibold">{clp(it.monto_pendiente)}</td>
          <td className="p-2 text-xs text-muted-foreground">{it.observacion}</td>
        </tr>
      );
    })}
    <tr className="bg-slate-100">
      <td colSpan={3} className="p-2 text-right text-xs font-semibold">Subtotal {g.proveedor} ({g.items.length})</td>
      <td className="p-2 text-right font-mono text-xs font-bold">{clp(g.subtotales.monto_pendiente)}</td>
      <td></td>
    </tr>
  </>
);
